import { getEnvironment } from '../utils.mjs';

import { buildCloudinaryUrl } from './cloudinary.js';
import { snakeCase } from './snake-case.js';

const { WB_ASSET_ENDPOINT, WB_ASSET_VIDEO_ENDPOINT } = getEnvironment();

const buildUrlQuery = (params) =>
  new URLSearchParams(Object.entries(params)).toString();

/*
 * Removes Cloudinary transformation parameters that are not supported
 * in wb-asset service.
 */
const wbAssetTrasformParams = (params) => {
  // Remove the following params:
  //   1. `secure` since it's always true
  const { secure, ...cloudinaryParams } = params;
  const pars = Object.fromEntries(
    Object.entries(cloudinaryParams).map(([key, value]) => [
      snakeCase(key),
      value,
    ])
  );

  // Note: this is a temporary client fix. We want to reset CDN cache for
  // images that were incorrectly cropped without having both width and height
  if (pars.crop === 'fill' && (!pars.height || !pars.width)) pars.crop = ''; // back end defaults to "fit" crop for no crop

  return pars;
};

// Videos have a separate endpoint that bypasses CDN
const chooseWbAssetEndpoint = (imageType) =>
  imageType === 'video' ? WB_ASSET_VIDEO_ENDPOINT : WB_ASSET_ENDPOINT;

/*
 * We lost uuid due to a bug on a lot of images so just fallback to parsing
 * uuid from publicId - same recovery AvatarImage does locally for avatar
 * images (FF_WBRT_5418_USE_UUID_AVATARS) and wb-admin-web's own
 * lib/wb-asset.js does for every asset; ported here (rather than shared)
 * since it's a 3-line leaf helper, consistent with how AvatarImage already
 * keeps its own copy instead of a shared import.
 */
const fetchUuidFromPublicId = (publicId) => {
  if (!publicId) return undefined;

  const matches = [...publicId.matchAll(/wb\/assets\/uuid\/([^/^?]+)/g)];
  return matches?.[0]?.[1];
};

/*
 * A wb-asset URL with its params exactly as given, in the given order - for
 * URLs that must match waybetter-mobile's own character for character (so
 * the hub shares the app's CDN cache), which wbAssetTrasformParams' crop
 * reset above would otherwise change.
 */
export const buildWbAssetParamsUrl = (params, imageType) =>
  `${chooseWbAssetEndpoint(imageType)}/wb/assets?${buildUrlQuery(params)}`;

export const buildWbAssetUuidUrl = (uuid, transformation, imageType) => {
  const params = {
    uuid: uuid,
    ...wbAssetTrasformParams(transformation),
  };

  const endpoint = chooseWbAssetEndpoint(imageType);
  return `${endpoint}/wb/assets?${buildUrlQuery(params)}`;
};

const _paramsForCloudinaryImage = (publicId, imageType, transformation) => {
  // Send both the original and the transformed files to wb-asset
  const cloudinaryUrl = buildCloudinaryUrl({ publicId, imageType });
  const transformedUrl = buildCloudinaryUrl({
    publicId,
    imageType,
    transforms: transformation,
  });

  return {
    cloudinary_url: cloudinaryUrl,
    transformed_cloudinary_url: transformedUrl,
    ...wbAssetTrasformParams(transformation),
  };
};

// For Contentful URLs, we just want to pass in the full URL to asset service
// as cloudinary_url and let asset service download the image.
// Note: also pass in transformed_cloudinary_url as a fallback
const _paramsForContentfulVideo = (contentfulUrl) => {
  // URLS usually start with "//" in Contentful JSON
  contentfulUrl = contentfulUrl.replace(/^\/\//, 'https://');

  return {
    cloudinary_url: contentfulUrl,
    transformed_cloudinary_url: contentfulUrl,
  };
};

/**
 * Redirects former cloudinary traffic through wb-asset-service so that we
 * could migrate images.
 *
 * Note: this only supports cloudinary images.
 * For videos, generate url with cloud.video_url(publicId, transformation)
 *
 * @param {object} options
 * @param {string} options.publicId - image ID in cloudinary (or wb-asset).
 * @param {string} options.uuid - image ID in wb-asset.
 * @param {string} options.imageType - either image or facebook.
 * @param {object} options.transformation - cloudinary transformation parameters
 * @returns {string} image URI
 */
export const wbAssetImageUrl = ({
  publicId,
  uuid,
  imageType,
  transformation,
}) => {
  imageType ||= 'image';
  transformation ||= {};

  if (uuid) return buildWbAssetUuidUrl(uuid, transformation, imageType);

  // Videos from videos.ctfassets.net were never hosted on Cloudinary so
  // we need to create a separate publicId for them
  const params = publicId.match('videos.ctfassets.net')
    ? _paramsForContentfulVideo(publicId)
    : _paramsForCloudinaryImage(publicId, imageType, transformation);

  const endpoint = chooseWbAssetEndpoint(imageType);
  return `${endpoint}/wb/assets?${buildUrlQuery(params)}`;
};

/**
 * Builds a playable/streamable URL for a VIDEO, hosted either by wb-asset
 * (uuid) or still natively on Cloudinary (publicId, e.g. a game upload made
 * before a wb-asset uuid was ever attached to it) - proxied through
 * wb-asset-service the same way wbAssetImageUrl does for images.
 *
 * Ported from wb-admin-web's own wbAssetVideoUrl (lib/wb-asset.js), not
 * folded into wbAssetImageUrl above - that function's own Cloudinary
 * fallback (_paramsForCloudinaryImage) has only ever been exercised for
 * actual images, and never forwards a resource_type, which silently builds
 * an image/upload Cloudinary path (see buildCloudinaryUrl/buildPath) even
 * for a video publicId. Ninja's version always forwards resource_type=video
 * instead, which is what actually makes the Cloudinary path (and the
 * backend's own asset lookup) resolve correctly for a video.
 *
 * @param {object} opts
 * @param {string} [opts.uuid] - video's id in wb-asset-service.
 * @param {string} [opts.publicId] - video's id in Cloudinary.
 * @param {object} [opts.transformation] - Cloudinary transform params (format, width, ...).
 * @returns {string} video URI
 */
export const wbAssetVideoUrl = ({ uuid, publicId, transformation }) => {
  transformation ||= {};
  const params = {
    resource_type: 'video',
    ...wbAssetTrasformParams(transformation),
  };

  uuid ??= fetchUuidFromPublicId(publicId);

  if (uuid) {
    params.uuid = uuid;
  } else if (publicId?.startsWith('https://videos.ctfassets.net')) {
    // Hub Contentful videos were never hosted on Cloudinary - pass the URL
    // through untransformed, same as _paramsForContentfulVideo above.
    params.cloudinary_url = publicId;
    params.transformed_cloudinary_url = publicId;
  } else {
    params.cloudinary_url = buildCloudinaryUrl({
      publicId,
      resourceType: 'video',
    });
    params.transformed_cloudinary_url = buildCloudinaryUrl({
      publicId,
      resourceType: 'video',
      transforms: transformation,
    });
  }

  // A jpg thumbnail is extracted through the plain (CDN-cached) endpoint,
  // same as any other image - only the actual mp4 stream needs the
  // video-streaming endpoint (bypasses CDN, see chooseWbAssetEndpoint).
  const endpoint =
    transformation.format === 'jpg'
      ? WB_ASSET_ENDPOINT
      : WB_ASSET_VIDEO_ENDPOINT;
  return `${endpoint}/wb/assets?${buildUrlQuery(params)}`;
};

export const validImage = (image) => !!(image?.publicId || image?.uuid);
